import React, { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import MotionReveal from "./MotionReveal";
import HomeHeroSystemVisual from "./HomeHeroSystemVisual";
import useMotionPreferences from "./useMotionPreferences";

gsap.registerPlugin(ScrollTrigger);

export default function HomeHero({ content }) {
  const sectionRef = useRef(null);
  const copyRef = useRef(null);
  const visualRef = useRef(null);
  const { reducedMotion } = useMotionPreferences();
  const titleLines = Array.isArray(content.titleLines) && content.titleLines.length
    ? content.titleLines
    : String(content.title || "").split("\n").filter(Boolean);
  const isStackedTitle = titleLines.length > 1;

  useLayoutEffect(() => {
    if (reducedMotion || !sectionRef.current) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.to(copyRef.current, {
        yPercent: 12,
        opacity: 0.46,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top+=220 top",
          end: "bottom top+=160",
          scrub: true,
        },
      });

      gsap.to(visualRef.current, {
        yPercent: -8,
        scale: 0.97,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top+=220 top",
          end: "bottom top+=160",
          scrub: true,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [reducedMotion]);

  return (
    <section ref={sectionRef} className="public-home-premium__hero" aria-labelledby="homepage-hero-title">
      <div className="mx-auto flex w-full max-w-page flex-col gap-12 px-4 pb-20 pt-32 sm:px-6 lg:px-8 lg:pb-28 lg:pt-36">
        <div className="public-home-premium__hero-grid grid gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(420px,0.98fr)] lg:items-start">
          <MotionReveal className="min-w-0" delay={0.05}>
            <div ref={copyRef} className="public-home-premium__hero-copy flex flex-col gap-5">
              <span className="public-home-premium__eyebrow public-home-premium__eyebrow--hero">
                {content.eyebrow}
              </span>

              <div className="public-home-premium__hero-copy-stack flex flex-col gap-5">
                <h1
                  id="homepage-hero-title"
                  className={`public-home-premium__hero-heading font-display text-white${isStackedTitle ? " public-home-premium__hero-heading--stacked" : ""}`}
                >
                  {titleLines.map((line, index) => (
                    <span key={`${line}-${index}`} className="public-home-premium__hero-heading-line">
                      {line}
                    </span>
                  ))}
                </h1>
                <p className="public-home-premium__hero-lead">
                  {content.lead}
                </p>
              </div>

              <div className="public-home-premium__hero-lower flex flex-col gap-4">
                <div className="public-home-premium__hero-actions flex flex-wrap gap-3">
                  {content.primaryActions.map((action) => (
                    <Link
                      key={action.label}
                      to={action.href}
                      className={`public-home-premium__cta public-home-premium__cta--${action.variant || "primary"}`}
                    >
                      {action.label}
                    </Link>
                  ))}
                </div>

                <div className="public-home-premium__hero-proof-rail" aria-label="Systemsignale im Hero">
                  <span className="public-home-premium__hero-proof-label">Systemsignale</span>
                  <div className="public-home-premium__hero-proof-items">
                    {content.proofItems.map((item, index) => (
                      <span key={item} className="public-home-premium__hero-pill">
                        <i aria-hidden="true">{String(index + 1).padStart(2, "0")}</i>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <nav className="public-home-premium__hero-subnav flex flex-wrap items-center gap-3 text-sm text-white/64" aria-label="Zusätzliche Hero-Navigation">
                  {content.secondaryActions.map((action) => {
                    if (action.href.startsWith("#")) {
                      return (
                        <a key={action.label} href={action.href} className="public-home-premium__subnav-link">
                          {action.label}
                        </a>
                      );
                    }

                    return (
                      <Link key={action.label} to={action.href} className="public-home-premium__subnav-link">
                        {action.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </MotionReveal>

          <div ref={visualRef}>
            <HomeHeroSystemVisual cards={content.stageCards} stats={content.stats} proofItems={content.proofItems} />
          </div>
        </div>
      </div>
    </section>
  );
}
